/**
 * AdminCategoriesTab — service_categories editor.
 *
 * Wave 5-Late Marketplace propagation. Executive discipline inherited
 * from anchor: Section chrome with hairlines, dense inline-edit rows,
 * muted "Deactivated" chip via Badge `shape="sharp"`, inputs on
 * `--ds-color-input-border` + 3px radius, tabular-nums on count spans.
 *
 * `AdminModal` (from `@/components/v2/AdminModal`) is retained — it's
 * already a shared shell used by other admin surfaces and is on the
 * v2-imports allow-list burndown.
 *
 * Business logic + immutable-slug rule + guardrail 422 handling +
 * testids preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useState } from "react";
import { EyeOff, Eye, Loader2, Plus, RefreshCw, Save, Tag, X } from "lucide-react";
import { toast } from "sonner";
import AdminModal from "@/components/v2/AdminModal";
import { api, formatApiError } from "@/api/client";
import { Badge } from "@/design-system/components/Badge";
import { AccentButton, GhostButton, Section, TextInput } from "@/admin/_primitives";

const SLUG_RE = /^[a-z][a-z0-9_]*$/;

/* ------------------------------------------------------------------ */
/* Local admin primitives.                                              */
/* ------------------------------------------------------------------ */





const GRID_COLS = "grid-cols-[160px_1fr_2fr_120px_150px]";

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function AdminCategoriesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/marketplace/categories");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(formatApiError(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  function startEdit(row) {
    setEditingId(row.id);
    setEditDraft({
      display_name: row.display_name,
      description: row.description || "",
      is_active: row.is_active,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft(null);
  }

  async function saveEdit(row) {
    if (!editDraft) return;
    setSavingId(row.id);
    try {
      const patch = {};
      if (editDraft.display_name !== row.display_name) patch.display_name = editDraft.display_name;
      if ((editDraft.description || "") !== (row.description || "")) {
        patch.description = editDraft.description || null;
      }
      if (editDraft.is_active !== row.is_active) patch.is_active = editDraft.is_active;
      if (!Object.keys(patch).length) {
        cancelEdit();
        return;
      }
      const { data } = await api.patch(`/admin/marketplace/categories/${row.id}`, patch);
      setRows((rs) => rs.map((r) => (r.id === row.id ? data : r)));
      cancelEdit();
      toast.success("Category saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "category_content_failed_guardrail") {
        toast.error("Category text contains disallowed language — please rephrase.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSavingId(null);
    }
  }

  async function toggleActive(row) {
    if (editingId === row.id) return;
    setTogglingId(row.id);
    const next = !row.is_active;
    try {
      const { data } = await api.patch(`/admin/marketplace/categories/${row.id}`, {
        is_active: next,
      });
      setRows((rs) => rs.map((r) => (r.id === row.id ? data : r)));
      toast.success(next ? "Category activated." : "Category deactivated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTogglingId(null);
    }
  }

  const listBadge = (
    <div className="flex items-center gap-2">
      <GhostButton
        onClick={fetchRows}
        data-testid="admin-category-refresh"
        className="!py-1 !text-[11px]"
      >
        <RefreshCw className="w-3 h-3" /> Refresh
      </GhostButton>
      <AccentButton
        onClick={() => setCreateOpen(true)}
        data-testid="admin-category-create-btn"
      >
        <Plus className="w-3.5 h-3.5" /> New category
      </AccentButton>
    </div>
  );

  return (
    <div>
      <div
        className="mb-2 text-[11px] uppercase tracking-[0.14em] tabular-nums"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        {rows.length} categor{rows.length === 1 ? "y" : "ies"} · slugs are immutable after create
      </div>

      {error && (
        <div
          className="mb-3 rounded-[var(--ds-radius-field)] border px-3 py-2 text-[12px]"
          style={{
            borderColor: "var(--ds-color-danger)",
            color: "var(--ds-color-danger)",
            background: "var(--ds-color-danger-soft)",
          }}
        >
          {error}
        </div>
      )}

      <Section
        title="Service categories"
        icon={Tag}
        badge={listBadge}
        testid="admin-category-list-section"
        bodyClassName="p-0"
      >
        <div className="overflow-x-auto" data-testid="admin-category-list">
          <div
            className={`grid ${GRID_COLS} gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.16em] font-semibold`}
            style={{
              color: "var(--ds-color-ink-subtle)",
              background: "var(--ds-color-admin-surface-muted)",
              borderBottom: "1px solid var(--ds-color-admin-border)",
            }}
          >
            <span>Slug (immutable)</span>
            <span>Display name</span>
            <span>Description</span>
            <span>Visibility</span>
            <span>Actions</span>
          </div>
          {loading ? (
            <div
              className="px-4 py-8 text-[13px] text-center"
              style={{ color: "var(--ds-color-ink-muted)" }}
              data-testid="admin-category-loading"
            >
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div
              className="px-4 py-10 text-[13px] text-center"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              No categories yet.
            </div>
          ) : (
            rows.map((row, idx) => {
              const editing = editingId === row.id;
              const busy = savingId === row.id;
              const isLast = idx === rows.length - 1;
              return (
                <div
                  key={row.id}
                  data-testid="admin-category-row"
                  data-category-id={row.id}
                  data-editing={editing ? "true" : "false"}
                  data-is-active={row.is_active ? "true" : "false"}
                  className={`grid ${GRID_COLS} gap-3 px-4 py-1.5 items-center text-[13px]`}
                  style={{
                    borderBottom: isLast
                      ? "none"
                      : "1px solid var(--ds-color-admin-border)",
                    opacity: row.is_active ? 1 : 0.62,
                  }}
                >
                  <span
                    className="font-mono text-[12px]"
                    style={{ color: "var(--ds-color-accent-strong)" }}
                  >
                    {row.slug}
                  </span>
                  {editing ? (
                    <>
                      <TextInput
                        data-testid="admin-category-edit-name"
                        value={editDraft.display_name}
                        onChange={(e) =>
                          setEditDraft((d) => ({ ...d, display_name: e.target.value }))
                        }
                      />
                      <TextInput
                        data-testid="admin-category-edit-description"
                        value={editDraft.description}
                        onChange={(e) =>
                          setEditDraft((d) => ({ ...d, description: e.target.value }))
                        }
                      />
                      <label
                        className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        <input
                          type="checkbox"
                          data-testid="admin-category-edit-active"
                          checked={editDraft.is_active}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, is_active: e.target.checked }))
                          }
                          className="accent-[var(--ds-color-accent)]"
                        />
                        Active
                      </label>
                      <div className="flex gap-1">
                        <AccentButton
                          data-testid="admin-category-edit-save"
                          onClick={() => saveEdit(row)}
                          disabled={busy}
                          className="!py-1 !text-[11px]"
                        >
                          <Save className="w-3 h-3" />
                          {busy ? "…" : "Save"}
                        </AccentButton>
                        <GhostButton
                          data-testid="admin-category-edit-cancel"
                          onClick={cancelEdit}
                          disabled={busy}
                          className="!py-1 !text-[11px]"
                        >
                          <X className="w-3 h-3" />
                        </GhostButton>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="flex items-center gap-2" style={{ color: "var(--ds-color-ink)" }}>
                        <span>{row.display_name}</span>
                        {!row.is_active && (
                          <Badge
                            tone="neutralMuted"
                            size="sm"
                            shape="sharp"
                            data-testid="admin-category-deactivated-tag"
                            className="uppercase tracking-[0.14em]"
                          >
                            Deactivated
                          </Badge>
                        )}
                      </span>
                      <span
                        className="truncate text-[12px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.description || "—"}
                      </span>
                      <GhostButton
                        data-testid="admin-category-toggle-active-btn"
                        data-next-state={row.is_active ? "deactivate" : "activate"}
                        onClick={() => toggleActive(row)}
                        disabled={togglingId === row.id}
                        title={row.is_active ? "Deactivate category" : "Activate category"}
                        aria-label={row.is_active ? "Deactivate category" : "Activate category"}
                        className="w-fit !py-1 !text-[11px]"
                      >
                        {togglingId === row.id ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" /> …
                          </>
                        ) : row.is_active ? (
                          <>
                            <EyeOff className="w-3 h-3" /> Deactivate
                          </>
                        ) : (
                          <>
                            <Eye className="w-3 h-3" /> Activate
                          </>
                        )}
                      </GhostButton>
                      <GhostButton
                        data-testid="admin-category-edit-btn"
                        onClick={() => startEdit(row)}
                        className="w-fit !py-1 !text-[11px]"
                      >
                        Edit
                      </GhostButton>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Section>

      <CategoryCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(cat) =>
          setRows((prev) =>
            [...prev, cat].sort((a, b) => a.display_name.localeCompare(b.display_name)),
          )
        }
      />
    </div>
  );
}

function CategoryCreateModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ slug: "", display_name: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [slugError, setSlugError] = useState(null);

  useEffect(() => {
    if (!open) {
      setForm({ slug: "", display_name: "", description: "" });
      setSubmitting(false);
      setError(null);
      setErrorCode(null);
      setSlugError(null);
    }
  }, [open]);

  function validate() {
    setSlugError(null);
    setError(null);
    if (!form.slug.trim()) {
      setSlugError("Slug is required.");
      return false;
    }
    if (!SLUG_RE.test(form.slug)) {
      setSlugError(
        "Slug must start with a lowercase letter and use only lowercase letters, digits, and underscores.",
      );
      return false;
    }
    if (!form.display_name.trim()) {
      setError("Display name is required.");
      return false;
    }
    return true;
  }

  async function submit() {
    if (!validate()) return;
    setSubmitting(true);
    setError(null);
    setErrorCode(null);
    try {
      const payload = {
        slug: form.slug.trim(),
        display_name: form.display_name.trim(),
      };
      if (form.description.trim()) payload.description = form.description.trim();
      const { data } = await api.post("/admin/marketplace/categories", payload);
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (status === 409 && code === "slug_conflict") {
        setSlugError("A category with this slug already exists.");
      } else if (code === "category_content_failed_guardrail") {
        setError("Category text contains disallowed language — please rephrase.");
        setErrorCode(code);
      } else {
        setError(formatApiError(err));
        setErrorCode("generic");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AdminModal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title="Add service category"
      description="Slugs are immutable after create. Choose carefully."
      testId="admin-category-create-modal"
      footer={
        <>
          <GhostButton
            onClick={onClose}
            disabled={submitting}
            data-testid="admin-category-create-cancel"
          >
            Cancel
          </GhostButton>
          <AccentButton
            onClick={submit}
            disabled={submitting}
            data-testid="admin-category-create-submit"
          >
            {submitting ? "Creating…" : "Create"}
          </AccentButton>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label
            className="text-[10.5px] uppercase tracking-[0.16em] font-semibold block mb-1"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Slug *
          </label>
          <TextInput
            data-testid="admin-category-create-slug"
            className="font-mono"
            value={form.slug}
            onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))}
            placeholder="e.g. marketing_strategy"
            style={{
              borderColor: slugError
                ? "var(--ds-color-danger)"
                : "var(--ds-color-input-border)",
            }}
          />
          {slugError && (
            <div
              data-testid="admin-category-create-slug-error"
              className="text-[11px] mt-1"
              style={{ color: "var(--ds-color-danger)" }}
            >
              {slugError}
            </div>
          )}
        </div>
        <div>
          <label
            className="text-[10.5px] uppercase tracking-[0.16em] font-semibold block mb-1"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Display name *
          </label>
          <TextInput
            data-testid="admin-category-create-name"
            value={form.display_name}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
          />
        </div>
        <div>
          <label
            className="text-[10.5px] uppercase tracking-[0.16em] font-semibold block mb-1"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Description (optional)
          </label>
          <textarea
            data-testid="admin-category-create-description"
            className="w-full rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[13px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
            style={{ borderColor: "var(--ds-color-input-border)", minHeight: 70 }}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </div>
        {error && (
          <div
            data-testid="admin-category-create-error"
            data-error-code={errorCode || ""}
            className="rounded-[var(--ds-radius-field)] border p-3 text-[13px]"
            style={{
              background: "var(--ds-color-danger-soft)",
              border: "1px solid var(--ds-color-danger)",
              color: "var(--ds-color-danger)",
            }}
          >
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}
